import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useState,
  useCallback,
  useEffect,
} from 'react';

import {
  VehicleDescription,
  World,
  PoliceSketch,
  Cross,
} from '../../../assets/js/svg/gui';
import {
  Column,
  Row,
} from '../../display';
import RoundTab from '../../RoundTab/RoundTab';
import SvgColorImg from '../../SvgColorImg';

const tabCodes = [
  'questionnaires',
  'questions',
  'statements',
];

const formCodes = [
  'policeSketch',
  'vehicleDescr',
  'locate',
  'picture',
];

const formLabel = {
  policeSketch: 'policeSketch',
  vehicleDescr: 'vehicle',
  locate: 'position',
  picture: 'pictures',
};

const formIcon = {
  policeSketch: <PoliceSketch />,
  vehicleDescr: <VehicleDescription />,
  locate: <World />,
  picture: <SvgColorImg src="/images/svg/gui/camera.svg" className="h-full w-full" imgColorAsCssFill />,
};

const questionIds = [
  'lifeThreat',
  'weaponThreat',
  'inProgress',
  'test',
];

const statementIds = {
  case: [
    'helpOnTheWay',
    'pingUs',
    'businessHours',
    'shouldBeOkClosing',
    'cantHelp',
  ],
  thanks: [
    'thanksForHelp',
    'gladCouldHelp',
    'doingOurJob',
    'thanksKindWords',
  ],
  cheering: [
    'rightToContactUs',
    'yourInYourRight',
    'goingToHelp',
    'hangInThere',
  ],
  moderation: [
    'stayPolite',
    'warningRespect',
  ],
};

function ChatQuestionnaireSelection({
  translate,
  setQuestion,
  setCompose,
  cancelQuestion,
}) {
  const [page, setPage] = useState('questionnaires');
  const [selection, setSelection] = useState();

  const handleTabClick = useCallback(
    (event) => {
      const { id } = event.currentTarget;
      console.log(`Click on ${id}`);
      setPage(id);
    },
    [],
  );

  const handleFormClick = useCallback(
    (event) => {
      const { id } = event.currentTarget;
      console.log(`Click on ${id}`);
      setSelection(id);
      setQuestion(id, formCodes.includes(id));
    },
    [setQuestion],
  );

  const handleStatementCatClick = useCallback(
    (event) => {
      const { id } = event.currentTarget;
      console.log(`Click on ${id}`);
      if (selection === id) setSelection();
      else setSelection(id);
    },
    [selection],
  );

  const handleStatementClick = useCallback(
    (event) => {
      const { id } = event.currentTarget;
      console.log(`Click on ${id}`);
      setCompose(translate(id, 'questionnairePrompts'));
    },
    [setCompose, translate],
  );

  useEffect(
    () => {
      console.log(`page=${page} selection=${selection}`);
    },
    [page, selection],
  );

  return (
    <div className="bg-pxGrey text-pxAccentBlue flex flex-col h-45 rounded-t-md overflow-hidden z-30">
      <div className="flex-1 flex flex-col">
        {/* Header with close button and tabs */}
        <div className="bg-pxDarkBlue text-pxAccentBlue flex items-center">
          <button
            type="button"
            className="h-4.5 w-4.5 flex justify-center items-center p-1 m-1.5"
            onClick={cancelQuestion}
          >
            <Cross className="h-full w-full fill-pxAccentBlue" />
          </button>

          <div className="flex flex-row justify-start flex-1 pt-1.5 items-end">
            {tabCodes.map((code) => (
              <button
                key={code}
                type="button"
                id={code}
                onClick={handleTabClick}
                disabled={page === code}
                className={classNames(
                  'flex-1',
                  {
                    'opacity-50': page === code,
                  },
                )}
              >
                <RoundTab
                  radiusPx={6}
                  backgroundColor={(page === code) ? '#4f6981' : '#182838'}
                  fill={(page === code) ? '#4f6981' : '#182838'}
                  className="h-4.5"
                  style={{ color: (page === code) ? '#77c0e0' : undefined }}
                >
                  <span className="px-0.5">
                    {translate(code, 'chat')}
                  </span>
                </RoundTab>
              </button>
            ))}
          </div>
        </div>

        {/* Content pages */}
        <div className="flex-1 overflow-hidden">
          {/* Questionnaires/Forms page */}
          {(page === 'questionnaires') && (
            <Row className="justify-evenly items-center p-1.5">
              {formCodes.map((code) => (
                <button
                  key={code}
                  type="button"
                  id={code}
                  className={classNames(
                    'flex-1 flex flex-col items-center',
                    {
                      'opacity-50': selection?.code === code,
                    },
                  )}
                  onClick={handleFormClick}
                  disabled={selection?.code === code}
                >
                  <div className="h-10 w-10 rounded-2.5 p-1.5 bg-pxAccentBlue relative mb-1.5 shadow-button">
                    <div className="h-full w-full fill-pxDarkBg1">
                      {formIcon[code]}
                    </div>
                  </div>
                  <span className="text-sm text-center">
                    {translate(formLabel[code], 'chat')}
                  </span>
                </button>
              ))}
            </Row>
          )}

          {/* Questions page */}
          {(page === 'questions') && (
            <Column className="flex-1 p-3">
              <div className="flex-1 flex flex-col justify-start overflow-auto bg-white text-black">
                {questionIds.map((code, index) => (
                  <div key={code} className="relative">
                    <button
                      type="button"
                      id={code}
                      className={classNames(
                        'w-full flex items-center justify-start p-1.5',
                        {
                          'bg-pxLightBlue': selection === code,
                          'opacity-50': selection === code,
                        },
                      )}
                      onClick={handleFormClick}
                      disabled={selection === code}
                    >
                      <span className="text-start">
                        {translate(code, 'questionnairePrompts')}
                      </span>
                    </button>

                    {(index < (questionIds.length - 1)) && (
                      <div className="absolute -bottom-1.5 left-0 py-1 px-5 w-full">
                        <div className="w-full h-0.5 bg-pxBgMuted" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Column>
          )}

          {/* Statements page */}
          {(page === 'statements') && (
            <Column className="flex-1 p-3">
              <div className="flex-1 flex flex-col justify-start overflow-auto bg-white text-black">
                {Object.keys(statementIds).map((code, index) => (
                  <div key={code}>
                    {/* Category button */}
                    <div className="relative">
                      <button
                        type="button"
                        id={code}
                        className={classNames(
                          'w-full flex items-center justify-start p-1.5',
                          {
                            'bg-pxLightBlue': selection === code,
                          },
                        )}
                        onClick={handleStatementCatClick}
                      >
                        <span className="text-start">
                          {`${translate(code, 'statementKinds')} ...`}
                        </span>
                      </button>

                      {(index < (Object.keys(statementIds).length - 1)) && (
                        <div className="absolute -bottom-1.5 left-0 py-1 px-5 w-full">
                          <div className="w-full h-0.5 bg-pxBgMuted" />
                        </div>
                      )}
                    </div>

                    {/* Expanded statements */}
                    {(selection === code) && statementIds[code].map((statement, sIndex) => (
                      <div key={statement} className="relative">
                        <button
                          type="button"
                          id={statement}
                          className="w-full flex items-center justify-start p-1.5"
                          onClick={handleStatementClick}
                        >
                          <span className="text-start truncate ml-2.5">
                            {`- ${translate(statement, 'questionnairePrompts')}`}
                          </span>
                        </button>

                        {(sIndex < (statementIds[code].length - 1)) && (
                          <div className="absolute -bottom-1.5 left-0 py-1 px-5 w-full">
                            <div className="w-full h-0.5 bg-pxBgMuted" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Column>
          )}
        </div>
      </div>
    </div>
  );
}

ChatQuestionnaireSelection.propTypes = {
  translate: PropTypes.func.isRequired,
  setQuestion: PropTypes.func.isRequired,
  setCompose: PropTypes.func.isRequired,
  cancelQuestion: PropTypes.func.isRequired,
};

export default ChatQuestionnaireSelection;
